import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Form,
  Table,
  Modal,
  Card as BootstrapCard,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FieldArray, Formik } from "formik";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import AsyncSelect from "react-select/async";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import "react-phone-input-2/lib/style.css";
import { IoEyeOutline } from "react-icons/io5";
import FadeLoaderComponent from "../../../components/loader-component";
import { roleTypes } from "../../../data/roleType";
import DatePickerField from "../../../components/input/date-picker-field";

const JobView = () => {
  const alertRef = useRef(null);
  const [job, setJob] = useState();
  const [jobData, setJobData] = useState({});
  const [systems, setSystems] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [systemPhasesMap, setSystemPhasesMap] = useState({});
  const [allContacts, setAllContacts] = useState({});
  const { id } = useParams();
  const [loader, setLoader] = useState(true);
  const navigate = useNavigate();
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getSystems = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(`${url.backendURL}system/all`, config);
      setSystems(response.data.response.data);
    } catch (err) {
      handleError(err, "information");
    }
  };

  const getContacts = async (stakeholderId) => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}stakeholder/${stakeholderId}/contacts`,
        config
      );
      const contactsData = response.data.response.data || [];
      setAllContacts((prev) => ({
        ...prev,
        [stakeholderId]: contactsData,
      }));
      return contactsData;
    } catch (err) {
      return [];
    }
  };

  const getStakeholders = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
        params: {
          id: id,
        },
      };
      const response = await axios.get(
        `${url.backendURL}stakeholder/all`,
        config
      );
      const stakeholdersData = response.data.response.data;
      setStakeholders(stakeholdersData);

      const contactsMap = {};
      for (const stakeholder of stakeholdersData) {
        if (stakeholder.id) {
          const contacts = await getContacts(stakeholder.id);
          contactsMap[stakeholder.id] = contacts;
        }
      }
    } catch (err) {
      handleError(err, "information");
    }
  };

  const getPhases = async (productSystemId) => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}product/system/${productSystemId}/phase`,
        config
      );
      return response.data.response.data;
    } catch (err) {
      handleError(err, "information");
      return [];
    }
  };

  const getJob = async () => {
    const config = {
      headers: {
        Authorization: `${token}`,
      },
    };

    try {
      const response = await axios.get(
        `${url.backendURL}job/${id}/detail`,
        config
      );
      const jobData = response.data.response.data;
      setJobData(jobData);

      const transformedContacts =
        jobData.stakeholders?.map((stakeholder) => ({
          stakeholderId: stakeholder.stakeholder_id,
          contactId: stakeholder.contact_id,
          type: stakeholder.type || "",
        })) || [];

      const transformedSystems =
        jobData.systems?.map((system) => ({
          systemId: system.system_id,
          phases: system.phase_ids || [],
        })) || [];

      setJob({
        name: jobData.name,
        location: jobData.location,
        estimate: jobData.estimate,
        bidDate: jobData.bid_date?.split("T")[0],
        jobStatus: jobData.job_status,
        siteName: jobData.site_name,
        systems: transformedSystems,
        activeSystemId: jobData.active_system_id,
        activePhaseId: jobData.active_phase_id,
        area: jobData.area && jobData.area.length > 0 ? jobData.area : [""],
        contacts: transformedContacts,
      });

      const phasesMap = {};
      for (const system of transformedSystems) {
        if (system.systemId) {
          const fetchedPhases = await getPhases(system.systemId);
          phasesMap[system.systemId] = fetchedPhases;
        }
      }
      setSystemPhasesMap(phasesMap);

      setLoader(false);
    } catch (err) {
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "information");
      setLoader(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([getJob(), getSystems(), getStakeholders()]);
      } catch (err) {}
    };
    if (decodedToken) {
      fetchData();
    }
  }, [decodedToken]);

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  return (
    <>
      <div>
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...job,
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            touched,
            setFieldValue,
            isSubmitting,
            resetForm,
          }) => (
            <Form>
              <Row>
                <Col xl="12" lg="8" ref={alertRef}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between">
                      <div
                        className="breadcrumb header-title"
                        style={{ cursor: "pointer" }}
                      >
                        <h4
                          onClick={() => navigate("/dashboard/app/job-list")}
                          className="text-primary card-title"
                          style={{ cursor: "pointer" }}
                        >
                          <span>Job</span>
                        </h4>
                        <h4 className="mx-2 card-title">{">"}</h4>
                        <h4 className="card-title">Details</h4>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                      {!loader ? (
                        <>
                          <Row>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="name"
                                placeholder="Enter project name"
                                value={values.name}
                                disabled={true}
                                label="Project name"
                              />
                            </Col>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="location"
                                placeholder="Enter address"
                                value={values.location}
                                disabled={true}
                                label="Address"
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="estimate"
                                placeholder="Enter estimate"
                                value={values.estimate}
                                disabled={true}
                                label="Estimate"
                                step="0.01"
                                pattern="^\d+(\.\d{0,2})?$"
                                min={0}
                              />
                            </Col>
                            <Col md={6}>
                              <DatePickerField
                                className="col-md-12 form-group"
                                name="bidDate"
                                placeholder="MM/DD/YYYY"
                                value={
                                  values?.bid_date
                                    ? values.bid_date.split("T")[0]
                                    : ""
                                }
                                onChange={() => {}}
                                disabled={true}
                                label="Start Date"
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="jobStatus"
                                placeholder="Enter job status"
                                value={values.job_status}
                                disabled={true}
                                label="Job Status"
                              />
                            </Col>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="siteName"
                                placeholder="Enter site name"
                                value={values.site_name}
                                disabled={true}
                                label="Site Name"
                              />
                            </Col>
                          </Row>

                          {values?.systems && values.systems.length > 0 ? (
                            <>
                              <Row className="mb-3 mt-4">
                                <Col md={12}>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="text-primary mb-0">
                                      Systems & Phases
                                    </h5>
                                  </div>
                                </Col>
                              </Row>
                              <BootstrapCard className="p-2 p-md-4 border rounded">
                                <FieldArray name="systems">
                                  {() => (
                                    <>
                                      {values.systems?.map((system, index) => {
                                        const phases =
                                          system.systemId &&
                                          systemPhasesMap[system.systemId]
                                            ? systemPhasesMap[system.systemId]
                                            : [];
                                        const systemName =
                                          systems.find(
                                            (s) => s.id === system.systemId
                                          )?.name || "";

                                        return (
                                          <div key={index} className="mb-4 p-3">
                                            <Row>
                                              <Col md={6}>
                                                <Form.Group className="form-group">
                                                  <Form.Label>
                                                    System
                                                  </Form.Label>
                                                  <Form.Control
                                                    type="text"
                                                    value={systemName}
                                                    disabled={true}
                                                    readOnly
                                                  />
                                                </Form.Group>
                                              </Col>
                                              <Col md={5}>
                                                <Form.Group className="">
                                                  <Form.Label>
                                                    Phases
                                                  </Form.Label>
                                                  <Form.Control
                                                    as="textarea"
                                                    disabled
                                                    rows={3}
                                                    value={
                                                      system.systemId
                                                        ? phases
                                                            .filter((phase) =>
                                                              system.phases.includes(
                                                                phase.id
                                                              )
                                                            )
                                                            .reduce(
                                                              (
                                                                acc,
                                                                phase,
                                                                index,
                                                                array
                                                              ) => {
                                                                const separator =
                                                                  (index + 1) %
                                                                    2 ===
                                                                  0
                                                                    ? "\n"
                                                                    : " | ";
                                                                return (
                                                                  acc +
                                                                  phase.name +
                                                                  (index ===
                                                                  array.length -
                                                                    1
                                                                    ? ""
                                                                    : separator)
                                                                );
                                                              },
                                                              ""
                                                            )
                                                        : ""
                                                    }
                                                    placeholder={
                                                      !system.systemId
                                                        ? "First select a system"
                                                        : phases.length === 0
                                                        ? "No phases available"
                                                        : "All phases selected"
                                                    }
                                                    style={{
                                                      resize: "none",
                                                      height: "5rem",
                                                      overflowX: "hidden",
                                                      backgroundColor: "#fff",
                                                      cursor: "not-allowed",
                                                      whiteSpace: "pre-wrap",
                                                    }}
                                                  />
                                                </Form.Group>
                                              </Col>
                                            </Row>
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}
                                </FieldArray>
                              </BootstrapCard>
                              <Row className="mb-3 mt-4">
                                <Col md={6}>
                                  <InputField
                                    className="col-md-12 form-group"
                                    type="text"
                                    name="activeSystem"
                                    value={jobData?.active_system_name || ""}
                                    disabled={true}
                                    label="Current Active System"
                                  />
                                </Col>
                                <Col md={6}>
                                  <InputField
                                    className="col-md-12 form-group"
                                    type="text"
                                    name="activePhase"
                                    value={jobData?.active_phase_name || ""}
                                    disabled={true}
                                    label="Current Active Phase"
                                  />
                                </Col>
                              </Row>
                            </>
                          ) : null}
                          <Row>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="start_date"
                                placeholder="Enter start date"
                                value={
                                  (values.start_date &&
                                    new Date(
                                      values.start_date
                                    ).toLocaleDateString("en-US", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                    })) ||
                                  "N/A"
                                }
                                disabled={true}
                                label="Start Date"
                              />
                            </Col>
                            <Col md={6}>
                              <InputField
                                className="col-md-12 form-group"
                                type="text"
                                name="end_date"
                                placeholder="N/A"
                                value={
                                  (values.end_date &&
                                    new Date(
                                      values.end_date
                                    ).toLocaleDateString("en-US", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                    })) ||
                                  "N/A"
                                }
                                disabled={true}
                                label="End Date"
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Form.Label htmlFor="area">Area</Form.Label>
                            <FieldArray
                              name="area"
                              render={(arrayHelpers) => (
                                <>
                                  {values?.area?.map((are, index) => {
                                    return (
                                      <React.Fragment key={index}>
                                        <Col
                                          md={6}
                                          className="d-flex flex-row align-items-start "
                                        >
                                          <Col md={10} className="mb-3">
                                            <Form.Control
                                              id={`area.${index}`}
                                              name={`area.${index}`}
                                              value={are}
                                              disabled={true}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                setFieldValue(
                                                  `area.${index}`,
                                                  value
                                                );
                                              }}
                                              onBlur={handleBlur}
                                              placeholder="e.g first floor"
                                              className="bg-white"
                                            />
                                          </Col>
                                        </Col>
                                      </React.Fragment>
                                    );
                                  })}
                                </>
                              )}
                            />
                          </Row>

                          {values?.contacts && values.contacts.length > 0 && (
                            <>
                              <Row className="mb-3 mt-4">
                                <Col md={12}>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="text-primary mb-0">
                                      Contacts
                                    </h5>
                                  </div>
                                </Col>
                              </Row>
                              <FieldArray name="contacts">
                                {() => (
                                  <>
                                    {values.contacts.map((contact, index) => {
                                      const stakeholder = stakeholders.find(
                                        (s) => s.id === contact.stakeholderId
                                      );
                                      const contactData = allContacts[
                                        contact.stakeholderId
                                      ]?.find(
                                        (c) => c.id === contact.contactId
                                      );

                                      return (
                                        <Row key={index} className="mb-3">
                                          <Col md={4}>
                                            <InputField
                                              className="col-md-12 form-group"
                                              type="text"
                                              name={`contacts.${index}.stakeholder`}
                                              value={stakeholder?.name || ""}
                                              disabled={true}
                                              label="Stakeholder"
                                            />
                                          </Col>
                                          <Col md={4}>
                                            <InputField
                                              className="col-md-12 form-group"
                                              type="text"
                                              name={`contacts.${index}.type`}
                                              value={contact.type || ""}
                                              disabled={true}
                                              label="Type"
                                            />
                                          </Col>
                                          <Col md={4}>
                                            <InputField
                                              className="col-md-12 form-group"
                                              type="text"
                                              name={`contacts.${index}.contact`}
                                              value={
                                                contactData
                                                  ? `${contactData.name}${
                                                      contactData.phone
                                                        ? ` - ${contactData.phone}`
                                                        : ""
                                                    }${
                                                      contactData.email
                                                        ? ` - ${contactData.email}`
                                                        : ""
                                                    }`
                                                  : ""
                                              }
                                              disabled={true}
                                              label="Contact"
                                            />
                                          </Col>
                                        </Row>
                                      );
                                    })}
                                  </>
                                )}
                              </FieldArray>
                            </>
                          )}
                        </>
                      ) : (
                        loader && <FadeLoaderComponent />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default JobView;
